import { useEffect, useRef, useState } from "react";
import { MessageSquareText, Radio, Download, Captions, Music2, Play, Loader2, FolderPlus, Share2, Clapperboard, Languages, Copy, Trash2, FileArchive, SlidersHorizontal, Video, Smartphone, Settings2, ClipboardList, Image as ImageIcon, Globe, ListOrdered, ImagePlus, X } from "lucide-react";
import SceneTimeline from "@/components/SceneTimeline";
import { Input } from "@/components/ui/input";
import { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import api, { API } from "@/lib/api";
import KeyArtCropper from "@/components/KeyArtCropper";


// Export options for a merged dialogue: pause between lines, optional Library track under, MP3 + SRT downloads.
const LANGS = ["English", "Français", "Español", "Deutsch", "Italiano", "Português", "日本語", "한국어", "中文", "Polski"];

export default function DialogueExportPanel({ dialogueId, seq, projectId, onProjectChanged, onTranslated }) {
  const [shares, setShares] = useState([]);
  const [busy, setBusy] = useState(null);
  const [lang, setLang] = useState("English");
  const [trProvider, setTrProvider] = useState("openai");
  const [translateOpen, setTranslateOpen] = useState(false);
  const [timelineOpen, setTimelineOpen] = useState(false);
  const [videoOpen, setVideoOpen] = useState(false);
  const [precaching, setPrecaching] = useState(false);
  const precache = async () => {
    setPrecaching(true);
    try {
      const { data } = await api.post(`/voice/dialogue/${dialogueId}/precache`, {});
      toast.success(`${data.total} réplique(s) prêtes pour le jeu — ${data.generated} générée(s), ${data.cache_hits} déjà en cache, ${data.credits_charged} crédit(s)${data.errors ? `, ${data.errors} erreur(s)` : ""}. Onglet « Voix runtime » → Voice Pack.`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Pré-génération impossible."); }
    finally { setPrecaching(false); }
  };
  const [subColor, setSubColor] = useState("#FFFFFF");
  const [subSize, setSubSize] = useState(26);
  const [subPos, setSubPos] = useState("bottom");
  const [second, setSecond] = useState("none");
  const [secondColor, setSecondColor] = useState("#00F0FF");
  const [family, setFamily] = useState([]);
  const [publishOpen, setPublishOpen] = useState(false);
  const [pubLangs, setPubLangs] = useState([]);
  const [pubProvider, setPubProvider] = useState("openai");
  const [waveform, setWaveform] = useState(true);
  const [keyart, setKeyart] = useState(false);
  const [focus, setFocus] = useState({});
  const [coverBust, setCoverBust] = useState(0);
  const saveFocus = async (side, pt) => {
    const next = { ...focus, [side]: pt };
    setFocus(next);
    try {
      await api.put(`/voice/dialogue/${dialogueId}/keyart/focus`, { [side]: pt });
      setCoverBust(Date.now());
      toast.success(side === "landscape" ? "Recadrage 16:9 enregistré" : "Recadrage 9:16 enregistré");
    } catch { toast.error("Recadrage non enregistré."); }
  };
  const uploadKeyart = async (file) => {
    if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try {
      await api.post(`/voice/dialogue/${dialogueId}/keyart`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setKeyart(true); setCoverBust(Date.now());
      toast.success("Key-art appliqué aux miniatures et vidéos de cette scène ✨");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Upload impossible."); }
  };
  const removeKeyart = async () => {
    try { await api.delete(`/voice/dialogue/${dialogueId}/keyart`); setKeyart(false); setCoverBust(Date.now()); toast.success("Fond sombre rétabli."); }
    catch { toast.error("Suppression impossible."); }
  };
  const loadFamily = () => api.get(`/voice/dialogue/${dialogueId}/family`).then(({ data }) => setFamily(data.filter((f) => f.dialogue_id !== dialogueId))).catch(() => {});
  const [pause, setPause] = useState(0.6);
  const [bg, setBg] = useState("none");
  const [gain, setGain] = useState(0.18);
  const [tracks, setTracks] = useState([]);
  const [projects, setProjects] = useState([]);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [attaching, setAttaching] = useState(false);
  const lastUrl = useRef(null);

  useEffect(() => {
    api.get("/generations").then(({ data }) => setTracks(data.filter((g) => g.status === "completed" && (g.audio_files?.mp3 || g.audio_files?.wav)))).catch(() => {});
    api.get("/projects").then(({ data }) => setProjects(data)).catch(() => {});
    api.get(`/voice/dialogue/${dialogueId}/shares`).then(({ data }) => setShares(data)).catch(() => {});
    api.get(`/voice/dialogue/${dialogueId}/keyart/status`).then(({ data }) => { setKeyart(!!data.keyart); setFocus(data.keyart_focus || {}); }).catch(() => {});
    loadFamily();
    return () => { if (lastUrl.current) URL.revokeObjectURL(lastUrl.current); };
  }, [dialogueId]);

  const opts = () => ({ pause, bg: bg === "none" ? null : bg, bg_gain: gain });
  const shareUrl = (t) => `${window.location.origin}/share/scene/${t}`;
  const copy = async (t) => { try { await navigator.clipboard.writeText(shareUrl(t)); toast.success("Lien copié ✨"); } catch { toast.message(shareUrl(t)); } };

  const share = async () => {
    setBusy("share");
    try {
      const { data } = await api.post(`/voice/dialogue/${dialogueId}/share`, opts());
      setShares((s) => [data, ...s]);
      await copy(data.token);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Partage impossible."); }
    finally { setBusy(null); }
  };

  const revoke = async (t) => {
    try { await api.delete(`/voice/shares/${t}`); setShares((s) => s.filter((x) => x.token !== t)); toast.success("Lien révoqué."); }
    catch { toast.error("Révocation impossible."); }
  };

  const makeCue = async () => {
    setBusy("cue");
    try {
      const { data } = await api.post(`/voice/dialogue/${dialogueId}/cue`, opts());
      toast.success(`Cue « ${data.name} » créé — mood ${data.mood} · ${data.genre}. Retrouvez-le dans Adaptive Gaming.`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Création du cue impossible."); }
    finally { setBusy(null); }
  };

  const translate = async () => {
    setBusy("translate");
    try {
      const { data } = await api.post(`/voice/dialogue/${dialogueId}/translate`, { language: lang, provider: trProvider });
      toast.success(`« ${data.title} » — ${data.lines.length} répliques en génération (${data.credits_used} cr).`);
      setTranslateOpen(false);
      onTranslated?.();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Traduction impossible."); }
    finally { setBusy(null); }
  };

  useEffect(() => { setPreviewUrl(null); }, [pause, bg, gain]);

  const preview = async () => {
    setPreviewLoading(true);
    try {
      const res = await fetch(qs(), { credentials: "include" });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || "export failed");
      if (lastUrl.current) URL.revokeObjectURL(lastUrl.current);
      const url = URL.createObjectURL(await res.blob());
      lastUrl.current = url;
      setPreviewUrl(url);
    } catch (e) {
      toast.error(typeof e.message === "string" ? e.message : "Aperçu impossible.");
    } finally { setPreviewLoading(false); }
  };

  const attach = async (pid) => {
    setAttaching(true);
    try {
      const { data } = await api.post(`/voice/dialogue/${dialogueId}/project`, { project_id: pid === "none" ? null : pid });
      toast.success(pid === "none" ? "Dialogue détaché du projet." : `Dialogue rattaché au projet (${data.lines} répliques) — inclus dans le ZIP.`);
      onProjectChanged?.();
    } catch { toast.error("Rattachement impossible."); }
    finally { setAttaching(false); }
  };

  const videoUrl = (vertical) => `${API}/voice/dialogue/${dialogueId}/export-video?pause=${pause}${bg !== "none" ? `&bg=${bg}&bg_gain=${gain}` : ""}&vertical=${vertical}&waveform=${waveform}&sub_color=${encodeURIComponent(subColor)}&sub_size=${subSize}&sub_pos=${subPos}${second !== "none" ? `&second=${second}&second_color=${encodeURIComponent(secondColor)}` : ""}`;
  const publish = async () => {
    if (pubLangs.length === 0) { toast.error("Choisissez au moins une langue."); return; }
    setBusy("publish");
    try {
      const { data } = await api.post(`/voice/dialogue/${dialogueId}/publish`, { languages: pubLangs, provider: pubProvider });
      toast.success(`${data.created.length} traduction(s) lancée(s)${data.skipped.length ? ` · déjà présentes : ${data.skipped.join(", ")}` : ""}. Les vidéos double sous-titre apparaissent ci-dessous dès que les voix sont prêtes.`);
      setPubLangs([]);
      onTranslated?.();
      setTimeout(loadFamily, 1500);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Publication impossible."); }
    finally { setBusy(null); }
  };
  const sendDigest = async () => {
    try {
      const { data } = await api.post("/voice/notes-digest");
      toast.success(data.sent ? "Résumé des notes ouvertes envoyé sur Slack ✨" : `Rien à envoyer — ${data.reason}.`);
    } catch { toast.error("Envoi du résumé impossible."); }
  };
  const qs = (extra = "") => `${API}/voice/dialogue/${dialogueId}/export?pause=${pause}${bg !== "none" ? `&bg=${bg}&bg_gain=${gain}` : ""}${extra}`;

  return (
    <div className="mt-2 rounded-xl border border-secondary/25 bg-secondary/[0.04] p-3 space-y-3" data-testid={`dialogue-export-panel-${seq}`}>
      <div className="grid sm:grid-cols-2 gap-4">
        <div className="space-y-1.5">
          <div className="flex justify-between text-xs"><Label>Pause entre les répliques</Label><span className="font-mono text-secondary" data-testid={`dialogue-pause-value-${seq}`}>{pause.toFixed(1)}s</span></div>
          <Slider value={[pause]} min={0.2} max={2} step={0.1} onValueChange={(v) => setPause(v[0])} data-testid={`dialogue-pause-${seq}`} />
        </div>
        <div className="space-y-1.5">
          <Label className="flex items-center gap-1.5"><Music2 size={12} className="text-secondary" /> Musique de fond (Librairie)</Label>
          <Select value={bg} onValueChange={setBg}>
            <SelectTrigger className="h-8 text-xs bg-background border-border" data-testid={`dialogue-bg-select-${seq}`}><SelectValue /></SelectTrigger>
            <SelectContent className="glass border-border max-h-64">
              <SelectItem value="none">Aucune</SelectItem>
              {tracks.map((t) => <SelectItem key={t.id} value={t.id}>#{t.seq} · {t.metadata?.title || t.prompt.slice(0, 40)}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>
      {bg !== "none" && (
        <div className="space-y-1.5">
          <div className="flex justify-between text-xs"><Label>Volume de la musique</Label><span className="font-mono text-secondary">{Math.round(gain * 100)}%</span></div>
          <Slider value={[gain]} min={0.05} max={0.6} step={0.01} onValueChange={(v) => setGain(v[0])} data-testid={`dialogue-bg-gain-${seq}`} />
        </div>
      )}
      <div className="flex flex-wrap items-center gap-2">
        <Button type="button" variant="outline" onClick={preview} disabled={previewLoading} className="h-9 border-primary/40 text-primary hover:bg-primary/10 gap-2" data-testid={`dialogue-preview-${seq}`}>
          {previewLoading ? <Loader2 className="animate-spin" size={14} /> : <Play size={14} />} Écouter la scène complète
        </Button>
        <a href={qs()} className="inline-flex items-center gap-2 rounded-md bg-secondary text-white hover:bg-secondary/90 h-9 px-3 text-sm font-medium transition-colors" title="MP3 avec chapitres ID3 (une réplique = un chapitre dans VLC, mpv, Apple Podcasts)" data-testid={`dialogue-export-mp3-${seq}`}>
          <Download size={14} /> Télécharger MP3{bg !== "none" ? " (scène musicale)" : ""}
        </a>
        <a href={qs("&format=srt")} className="inline-flex items-center gap-2 rounded-md border border-secondary/40 text-secondary hover:bg-secondary/10 h-9 px-3 text-sm font-medium transition-colors" data-testid={`dialogue-export-srt-${seq}`}>
          <Captions size={14} /> Sous-titres SRT
        </a>
        <a href={videoUrl(false)} className="inline-flex items-center gap-2 rounded-md bg-primary text-primary-foreground hover:bg-primary/90 h-9 px-3 text-sm font-medium transition-colors" title="MP4 1280x720, sous-titres incrustés, logo — pour trailers et pitch decks" data-testid={`dialogue-export-video-${seq}`}>
          <Video size={14} /> Vidéo MP4 16:9
        </a>
        <a href={videoUrl(true)} className="inline-flex items-center gap-2 rounded-md border border-primary/40 text-primary hover:bg-primary/10 h-9 px-3 text-sm font-medium transition-colors" title="MP4 1080x1920 vertical — TikTok, Reels, Shorts" data-testid={`dialogue-export-video-vertical-${seq}`}>
          <Smartphone size={14} /> Vidéo verticale 9:16
        </a>
        <a href={`${API}/voice/dialogue/${dialogueId}/chapters.txt?pause=${pause}`} className="inline-flex items-center gap-2 rounded-md border border-border text-foreground hover:bg-muted h-9 px-3 text-sm font-medium transition-colors" title="Chapitres YouTube (0:00 Personnage: réplique) — aussi intégrés dans le MP4" data-testid={`dialogue-chapters-${seq}`}>
          <ListOrdered size={14} /> Chapitres
        </a>
        <a href={`${API}/voice/dialogue/${dialogueId}/cover.png?v=${coverBust}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 rounded-md border border-border text-foreground hover:bg-muted h-9 px-3 text-sm font-medium transition-colors" title="Miniature PNG 1280x720 (logo, titre, 1ʳᵉ réplique)" data-testid={`dialogue-cover-${seq}`}>
          <ImageIcon size={14} /> Miniature PNG
        </a>
        <Button type="button" variant="ghost" onClick={() => setVideoOpen((o) => !o)} className="h-9 gap-1.5 text-muted-foreground hover:text-foreground" data-testid={`video-options-open-${seq}`}>
          <Settings2 size={14} /> Sous-titres
        </Button>
        <Button type="button" variant="ghost" onClick={() => setPublishOpen((o) => !o)} className="h-9 gap-1.5 text-muted-foreground hover:text-foreground" data-testid={`publish-open-${seq}`}>
          <Globe size={14} /> Publication multilingue
        </Button>
        <Button type="button" variant="outline" onClick={precache} disabled={precaching} className="h-9 gap-2 border-primary/40 text-primary hover:bg-primary/10" title="Envoie toutes les répliques dans le cache runtime (même voix, même émotion) : le jeu les obtient instantanément via Speak() / le Voice Pack. 2 crédits par réplique nouvelle, 0 si déjà en cache." data-testid={`dialogue-precache-${seq}`}>
          {precaching ? <Loader2 size={14} className="animate-spin" /> : <Radio size={14} />} Pré-générer pour le jeu
        </Button>
        <a href={`${API}/voice/dialogue/${dialogueId}/lipsync-bundle?pause=${pause}`} className="inline-flex items-center gap-2 rounded-md border border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10 h-9 px-3 text-sm font-medium transition-colors" title="Audio par réplique + lipsync.json (mots, visèmes Oculus, offsets de scène) — la cutscene part avec le timing des bouches" data-testid={`dialogue-export-lipsync-${seq}`}>
          <MessageSquareText size={14} /> Lipsync (ZIP)
        </a>
        <a href={`${API}/voice/dialogue/${dialogueId}/srt-bundle?pause=${pause}`} className="inline-flex items-center gap-2 rounded-md border border-border text-foreground hover:bg-muted h-9 px-3 text-sm font-medium transition-colors" title="SRT de la scène et de toutes ses traductions" data-testid={`dialogue-export-srt-bundle-${seq}`}>
          <FileArchive size={14} /> SRT multilingues (ZIP)
        </a>
        <Button type="button" variant={timelineOpen ? "default" : "outline"} onClick={() => setTimelineOpen((o) => !o)} className={`h-9 gap-2 ${timelineOpen ? "bg-primary text-primary-foreground" : "border-primary/40 text-primary hover:bg-primary/10"}`} data-testid={`timeline-open-${seq}`}>
          <SlidersHorizontal size={14} /> Studio de scène
        </Button>
      </div>
      {videoOpen && (
        <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-3 rounded-lg border border-border bg-background p-3" data-testid={`video-options-${seq}`}>
          <div className="space-y-1">
            <Label className="text-xs flex items-center gap-1.5"><ImagePlus size={11} /> Key-art (fond)</Label>
            <div className="flex items-center gap-2">
              <label className="h-8 px-3 rounded-md border border-border text-xs inline-flex items-center gap-1.5 cursor-pointer hover:bg-muted" data-testid={`video-keyart-label-${seq}`}>
                <ImagePlus size={12} /> {keyart ? "Remplacer" : "Importer"}
                <input type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={(e) => { uploadKeyart(e.target.files?.[0]); e.target.value = ""; }} data-testid={`video-keyart-${seq}`} />
              </label>
              {keyart && <button type="button" onClick={removeKeyart} className="text-muted-foreground hover:text-destructive" title="Retirer le key-art" data-testid={`video-keyart-remove-${seq}`}><X size={13} /></button>}
            </div>
            <div className="flex items-end gap-2 pt-1" data-testid={`video-keyart-preview-${seq}`}>
              <figure className="space-y-0.5">
                <img src={`${API}/voice/dialogue/${dialogueId}/cover.png?v=${coverBust}`} alt="Aperçu du rendu 16:9" className="h-14 w-24 rounded-md border border-primary/30 object-cover" data-testid={`video-cover-thumb-${seq}`} />
                <figcaption className="text-[9px] font-mono text-muted-foreground">{keyart ? "Rendu 16:9" : "Fond sombre (défaut)"}</figcaption>
              </figure>
              {keyart && (
                <figure className="space-y-0.5">
                  <img src={`${API}/voice/dialogue/${dialogueId}/cover.png?vertical=true&v=${coverBust}`} alt="Aperçu du rendu 9:16" className="h-14 w-8 rounded-md border border-primary/30 object-cover" data-testid={`video-cover-thumb-vertical-${seq}`} />
                  <figcaption className="text-[9px] font-mono text-muted-foreground">9:16</figcaption>
                </figure>
              )}
            </div>
            {keyart && (
              <div className="flex flex-wrap items-start gap-3 pt-1" data-testid={`video-keyart-crop-${seq}`}>
                <KeyArtCropper src={`${API}/voice/dialogue/${dialogueId}/keyart?v=${coverBust}`} aspect={16 / 9} value={focus.landscape} onChange={(pt) => saveFocus("landscape", pt)} label="16:9" testId={`video-keyart-crop-landscape-${seq}`} />
                <KeyArtCropper src={`${API}/voice/dialogue/${dialogueId}/keyart?v=${coverBust}`} aspect={9 / 16} value={focus.portrait} onChange={(pt) => saveFocus("portrait", pt)} label="9:16" testId={`video-keyart-crop-portrait-${seq}`} />
              </div>
            )}
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Waveform animée</Label>
            <button type="button" onClick={() => setWaveform((w) => !w)} className={`h-8 px-3 rounded-md border text-xs font-mono ${waveform ? "border-secondary/50 text-secondary bg-secondary/10" : "border-border text-muted-foreground"}`} data-testid={`video-waveform-${seq}`}>{waveform ? "ON" : "OFF"}</button>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Couleur</Label>
            <div className="flex items-center gap-2">
              <input type="color" value={subColor} onChange={(e) => setSubColor(e.target.value)} className="h-8 w-10 rounded border border-border bg-card cursor-pointer" data-testid={`video-sub-color-${seq}`} />
              <span className="font-mono text-xs text-muted-foreground">{subColor}</span>
            </div>
          </div>
          <div className="space-y-1">
            <div className="flex justify-between text-xs"><Label>Taille</Label><span className="font-mono text-primary">{subSize}</span></div>
            <Slider value={[subSize]} min={16} max={44} step={1} onValueChange={(v) => setSubSize(v[0])} data-testid={`video-sub-size-${seq}`} />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Position</Label>
            <Select value={subPos} onValueChange={setSubPos}>
              <SelectTrigger className="h-8 text-xs bg-card border-border" data-testid={`video-sub-pos-${seq}`}><SelectValue /></SelectTrigger>
              <SelectContent className="glass border-border">
                <SelectItem value="bottom">Bas</SelectItem><SelectItem value="center">Centre</SelectItem><SelectItem value="top">Haut</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label className="text-xs flex items-center gap-1.5"><Languages size={11} /> 2ᵉ langue (double sous-titre)</Label>
            <div className="flex items-center gap-2">
              <Select value={second} onValueChange={setSecond}>
                <SelectTrigger className="h-8 text-xs bg-card border-border flex-1" data-testid={`video-second-${seq}`}><SelectValue /></SelectTrigger>
                <SelectContent className="glass border-border">
                  <SelectItem value="none">Aucune</SelectItem>
                  {family.map((f) => <SelectItem key={f.dialogue_id} value={f.dialogue_id}>{f.language}</SelectItem>)}
                </SelectContent>
              </Select>
              {second !== "none" && <input type="color" value={secondColor} onChange={(e) => setSecondColor(e.target.value)} className="h-8 w-10 rounded border border-border bg-card cursor-pointer" data-testid={`video-second-color-${seq}`} />}
            </div>
            {family.length === 0 && <p className="text-[10px] text-muted-foreground">Traduisez la scène pour activer le double sous-titrage.</p>}
          </div>
        </div>
      )}
      {publishOpen && (
        <div className="rounded-lg border border-border bg-background p-3 space-y-3" data-testid={`publish-panel-${seq}`}>
          <div className="text-[10px] font-mono text-primary flex items-center gap-1.5"><Globe size={12} /> PUBLICATION MULTILINGUE — traduisez en un clic, puis téléchargez chaque vidéo en double sous-titre</div>
          <div className="flex flex-wrap gap-1.5">
            {LANGS.map((l) => {
              const has = family.some((f) => f.language.toLowerCase() === l.toLowerCase());
              const on = pubLangs.includes(l);
              return (
                <button key={l} type="button" disabled={has} onClick={() => setPubLangs((arr) => (on ? arr.filter((x) => x !== l) : [...arr, l]))}
                  className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${has ? "border-emerald-500/30 text-emerald-400 opacity-70" : on ? "border-primary bg-primary/15 text-primary" : "border-border text-muted-foreground hover:text-foreground"}`} data-testid={`publish-lang-${l}`}>
                  {has ? "✓ " : ""}{l}
                </button>
              );
            })}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <button type="button" onClick={() => setPubProvider((p) => (p === "openai" ? "claude" : "openai"))} className="text-[10px] font-mono px-2.5 py-1 rounded-full bg-primary/15 text-primary border border-primary/30" data-testid={`publish-provider-${seq}`}>{pubProvider === "openai" ? "ChatGPT" : "Claude"}</button>
            <Button size="sm" onClick={publish} disabled={busy === "publish" || pubLangs.length === 0} className="h-8 bg-primary text-primary-foreground hover:bg-primary/90 gap-1.5" data-testid={`publish-submit-${seq}`}>
              {busy === "publish" ? <Loader2 className="animate-spin" size={13} /> : <Globe size={13} />} Traduire {pubLangs.length || ""} langue{pubLangs.length > 1 ? "s" : ""}
            </Button>
            <button type="button" onClick={loadFamily} className="text-[11px] text-muted-foreground hover:underline" data-testid={`publish-refresh-${seq}`}>Actualiser</button>
          </div>
          {family.length > 0 && (
            <div className="space-y-1" data-testid={`publish-family-${seq}`}>
              {family.map((f) => (
                <div key={f.dialogue_id} className="flex flex-wrap items-center gap-2 text-xs rounded-lg border border-border bg-card px-2.5 py-1.5">
                  <span className="font-semibold w-24">{f.language}</span>
                  {f.completed ? (
                    <>
                      <a href={videoUrl(false).replace(/&second=[^&]*(&second_color=[^&]*)?/, "") + `&second=${f.dialogue_id}&second_color=${encodeURIComponent(secondColor)}`} className="text-primary hover:underline inline-flex items-center gap-1" data-testid={`publish-video-${f.dialogue_id}`}><Video size={11} /> MP4 16:9 double sous-titre</a>
                      <a href={videoUrl(true).replace(/&second=[^&]*(&second_color=[^&]*)?/, "") + `&second=${f.dialogue_id}&second_color=${encodeURIComponent(secondColor)}`} className="text-primary hover:underline inline-flex items-center gap-1"><Smartphone size={11} /> 9:16</a>
                      <a href={`${API}/voice/dialogue/${f.dialogue_id}/export?pause=${pause}&format=srt`} className="text-muted-foreground hover:underline inline-flex items-center gap-1"><Captions size={11} /> SRT</a>
                    </>
                  ) : <span className="text-muted-foreground inline-flex items-center gap-1"><Loader2 className="animate-spin" size={11} /> voix en génération…</span>}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
      {timelineOpen && <SceneTimeline dialogueId={dialogueId} seq={seq} tracks={tracks} />}
      {previewUrl && (
        <audio src={previewUrl} controls autoPlay className="w-full h-10" data-testid={`dialogue-preview-audio-${seq}`} />
      )}
      <div className="flex flex-wrap items-center gap-2 pt-2 border-t border-border/60">
        <Button type="button" variant="outline" size="sm" onClick={share} disabled={busy === "share"} className="h-8 border-primary/40 text-primary hover:bg-primary/10 gap-1.5" data-testid={`scene-share-${seq}`}>
          {busy === "share" ? <Loader2 className="animate-spin" size={13} /> : <Share2 size={13} />} Lien public
        </Button>
        <Button type="button" variant="outline" size="sm" onClick={makeCue} disabled={busy === "cue"} className="h-8 border-secondary/40 text-secondary hover:bg-secondary/10 gap-1.5" data-testid={`scene-cue-${seq}`}>
          {busy === "cue" ? <Loader2 className="animate-spin" size={13} /> : <Clapperboard size={13} />} Cue cutscene Gaming
        </Button>
        <Button type="button" variant="outline" size="sm" onClick={() => setTranslateOpen((o) => !o)} className="h-8 border-border text-foreground hover:bg-muted gap-1.5" data-testid={`scene-translate-open-${seq}`}>
          <Languages size={13} /> Traduire la scène
        </Button>
        <Button type="button" variant="ghost" size="sm" onClick={sendDigest} className="h-8 gap-1.5 text-muted-foreground hover:text-foreground" title="Envoie maintenant sur Slack le résumé des notes ouvertes (auto chaque lundi 9h UTC)" data-testid={`notes-digest-${seq}`}>
          <ClipboardList size={13} /> Résumé Slack des notes
        </Button>
      </div>
      {translateOpen && (
        <div className="flex flex-wrap items-center gap-2 rounded-lg border border-border bg-background p-2.5" data-testid={`scene-translate-panel-${seq}`}>
          <Select value={lang} onValueChange={setLang}>
            <SelectTrigger className="h-8 text-xs w-36 bg-card border-border" data-testid={`scene-translate-lang-${seq}`}><SelectValue /></SelectTrigger>
            <SelectContent className="glass border-border">{LANGS.map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}</SelectContent>
          </Select>
          <Input value={lang} onChange={(e) => setLang(e.target.value)} placeholder="ou une autre langue…" className="h-8 text-xs w-40 bg-card border-border" data-testid={`scene-translate-custom-${seq}`} />
          <button type="button" onClick={() => setTrProvider((p) => (p === "openai" ? "claude" : "openai"))} className="text-[10px] font-mono px-2.5 py-1 rounded-full bg-primary/15 text-primary border border-primary/30" data-testid={`scene-translate-provider-${seq}`}>
            {trProvider === "openai" ? "ChatGPT" : "Claude"}
          </button>
          <Button size="sm" onClick={translate} disabled={busy === "translate" || !lang.trim()} className="h-8 bg-primary text-primary-foreground hover:bg-primary/90 gap-1.5" data-testid={`scene-translate-submit-${seq}`}>
            {busy === "translate" ? <Loader2 className="animate-spin" size={13} /> : <Languages size={13} />} Traduire & régénérer · 2 cr/réplique
          </Button>
          <span className="text-[11px] text-muted-foreground">Mêmes voix et émotions, nouvelle scène « (langue) ».</span>
        </div>
      )}
      {shares.length > 0 && (
        <div className="space-y-1" data-testid={`scene-shares-${seq}`}>
          {shares.map((s) => (
            <div key={s.token} className="flex items-center gap-2 text-[11px] font-mono rounded-lg bg-background border border-border px-2.5 py-1.5">
              <Share2 size={11} className="text-primary shrink-0" />
              <a href={shareUrl(s.token)} target="_blank" rel="noreferrer" className="truncate flex-1 text-primary hover:underline" data-testid={`scene-share-link-${s.token}`}>{shareUrl(s.token)}</a>
              <span className="text-muted-foreground">{s.plays || 0} écoute{(s.plays || 0) > 1 ? "s" : ""}{s.comments ? ` · ${s.comments} note${s.comments > 1 ? "s" : ""}` : ""}{s.with_music ? " · musique" : ""}</span>
              <button type="button" onClick={() => copy(s.token)} className="text-muted-foreground hover:text-foreground" title="Copier" data-testid={`scene-share-copy-${s.token}`}><Copy size={12} /></button>
              <button type="button" onClick={() => revoke(s.token)} className="text-muted-foreground hover:text-destructive" title="Révoquer" data-testid={`scene-share-revoke-${s.token}`}><Trash2 size={12} /></button>
            </div>
          ))}
        </div>
      )}
      <div className="flex flex-wrap items-center gap-2 pt-2 border-t border-border/60">
        <Label className="flex items-center gap-1.5 text-xs"><FolderPlus size={12} className="text-primary" /> Projet</Label>
        <Select value={projectId || "none"} onValueChange={attach} disabled={attaching}>
          <SelectTrigger className="h-8 text-xs bg-background border-border w-64" data-testid={`dialogue-project-select-${seq}`}><SelectValue /></SelectTrigger>
          <SelectContent className="glass border-border max-h-64">
            <SelectItem value="none">Aucun projet</SelectItem>
            {projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <span className="text-[11px] text-muted-foreground">Toutes les répliques rejoignent le ZIP du projet (dossier dialogue + script.txt).</span>
      </div>
    </div>
  );
}
